import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconMap from '../../lib/icons/IconMap';
import { IconShape } from '../../components';
import imgNe from '../../assets/chooseus/img.ne.jpg';
import imgQs from '../../assets/chooseus/img.qs.jpg';
import imgCc from '../../assets/chooseus/img.ccl.jpg';
import imgSa from '../../assets/chooseus/img.sas.jpg';
import imgHn from '../../assets/chooseus/img.hn.jpg';
import imgFh from '../../assets/chooseus/img.fh.jpg';

interface ProgramCard {
	id: number;
	title: string;
	description: string;
	image: string;
	// linkText: string;
	icon: any;
}

export const Features = () => {
	const programs: ProgramCard[] = [
		{
			id: 1,
			title: 'NURTURING ENVIRONMENT',
			description: 'We provide a caring, supportive atmosphere where every child feels valued, confident, and inspired to explore. Our focus on emotional wellbeing ensures children thrive both socially and academically.',
			image: imgNe,
			// linkText: 'LEARN MORE',
			icon: IconMap.faHeart
		},
		{
			id: 2,
			title: 'QUALIFIED STAFF',
			description: 'Our team is made up of experienced, dedicated educators who are passionate about early years and after-school care. With over 29 years of combined experience, they provide consistent guidance and encouragement for every child.',
			image: imgQs,
			// linkText: 'LEARN MORE',
			icon: IconMap.faUsers
		},
		{
			id: 3,
			title: 'CHILD-CENTRED LEARNING',
			description: 'Learning at Childs play is tailored to each child\'s interests, abilities, and developmental stage. Through play, creativity, and curiosity-driven activities, children develop skills that last a lifetime.',
			image: imgCc,
			// linkText: 'LEARN MORE',
			icon: IconMap.faBookOpen
		},
		{
			id: 4,
			title: 'SAFE AND SECURE',
			description: 'Safety is our top priority. From secure premises to attentive supervision, we ensure children are always protected while enjoying a stimulating, worry-free environment.',
			image: imgSa,
			// linkText: 'LEARN MORE',
			icon: IconMap.faShieldAlt
		},
		{
			id: 5,
			title: 'HEALTHY NUTRITION',
			description: 'We provide balanced, nutritious snacks that support children\'s growth and energy levels. Healthy eating habits are encouraged through fun, child-friendly options.',
			image: imgHn,
			// linkText: 'LEARN MORE',
			icon: IconMap.faAppleAlt
		},
		{
			id: 6,
			title: 'FLEXIBLE HOURS',
			description: 'Our services are designed to fit around busy family schedules. From breakfast club to after-school care, we offer flexibility and reliability to meet the needs of every household.',
			image: imgFh,
			// linkText: 'LEARN MORE',
			icon: IconMap.faClock
		}
	];

	return (
		<div className="bg-gray-50">
		<section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
			<div className="max-w-7xl mx-auto">
				{/* Header Section */}
				<div className="text-center mb-12">
					<button className="button-offer">Why Choose Us</button>
					<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 font-['Quicksand']">
					Creating Happy Memories & Bright Futures
					</h2>
					<p className="text-gray-500 text-base md:text-lg max-w-5xl mx-auto font-['DM_Sans']">
					At Childs Play, we bring over 30 years of trusted, family-run childcare experience, 
					offering a safe, nurturing, and inspiring environment for every child. 
					Our dedicated educators work closely with families to support learning, creativity, 
					and social development through a warm, home-from-home atmosphere. 
					Choosing Childs Play means choosing quality, consistency, and a caring community where 
					children can truly thrive.
					</p>
				</div>

				{/* Programs Grid - 2 cards on top, 4 on bottom */}
				<div className="space-y-6">
					{/* top two cards */}
					<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
						{programs.slice(0, 2).map((program, index) => {
							// Different colors for each icon
							const iconColors = [
								'group-hover:text-blue-600',
								'group-hover:text-purple-600',
								'group-hover:text-green-600',
								'group-hover:text-pink-600',
								'group-hover:text-orange-600',
								'group-hover:text-teal-600'
							];
							const hoverColor = iconColors[index];

							return (
							<div key={index}>
								<div className='feature-card feature-card-horizontal group'>
									{/* Image */}
									<div className="feature-card-image-container feature-card-horizontal-image">
										<img src={program.image} alt={program.title} className='feature-card-image' />
										<div className="feature-card-image-overlay"></div>
									</div>

									{/* content */}
									<div className="feature-card-content feature-card-horizontal-content">
										{/* Mobile: vertical layout like bottom cards */}
										<div className='feature-card-mobile-icon-title'>
											<IconShape color="#FFFFFF" width={78} height={64} className='feature-card-icon-shape'>
												<FontAwesomeIcon
													icon={program.icon} 
													className={`feature-card-icon ${hoverColor} transition-colors duration-300`}
												/>
											</IconShape>
											<h3 className="feature-card-title">{program.title}</h3>
										</div>

										{/* Desktop: horizontal layout */}
										<div className='feature-card-horizontal-icon-title z-10'>
											<IconShape color="#FFFFFF" width={78} height={64} rotation={0}>
												<FontAwesomeIcon
													icon={program.icon} 
													className={`feature-card-icon ${hoverColor} transition-colors duration-300`}
												/>
											</IconShape>
											<h3 className="feature-card-title feature-card-horizontal-title">{program.title}</h3>
										</div>

										<div className='feature-card-horizontal-description'>
											<p className="feature-card-description">
												{program.description}
											</p>

											{/* <a href="#" className="feature-card-link"> */}
												{/* <span>{program.linkText}</span> */}
												{/* <FontAwesomeIcon icon={IconMap.faArrowRight} className="w-4 h-4" /> */}
											{/* </a> */}
										</div>
									</div>
								</div>
							</div>
							);
						})}
					</div>

					{/* bottom four cards */}
					<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
						{programs.slice(2, 6).map((program, index) => {
							// Different colors for each icon (continuing from top cards)
							const iconColors = [
								'group-hover:text-blue-600',
								'group-hover:text-purple-600',
								'group-hover:text-green-600',
								'group-hover:text-pink-600',
								'group-hover:text-orange-600',
								'group-hover:text-teal-600'
							];
							const hoverColor = iconColors[index + 2]; // +2 because we're starting from index 2

							return (
								<div key={index}>
								<div className='feature-card group'>
									{/* Image */}
									<div className="feature-card-image-container">
										<img src={program.image} alt={program.title} className='feature-card-image' />
										<div className="feature-card-image-overlay"></div>
									</div>

									{/* content */}
									<div className="feature-card-content">
										<div className='feature-card-icon-title-container'>
											<IconShape color="#FFFFFF" width={78} height={64} className='feature-card-icon-shape'>
												<FontAwesomeIcon
													icon={program.icon} 
													className={`feature-card-icon ${hoverColor} transition-colors duration-300`}
												/>
											</IconShape>
											<h3 className="feature-card-title">{program.title}</h3>
										</div>

										<div>
											<p className="feature-card-description">
												{program.description}
											</p>

											{/* <a href="#" className="feature-card-link"> */}
												{/* <span>{program.linkText}</span> */}
												{/* <FontAwesomeIcon icon={IconMap.faArrowRight} className="w-4 h-4" /> */}
											{/* </a> */}
										</div>
									</div>
								</div>
							</div>
							);
						})}
					</div>
				</div>
			</div>
		</section>
		</div>
	);
};

export default Features;